import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import { BRAND } from "@/lib/site";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white" data-testid="site-navbar">
      {/* Utility bar */}
      <div className="hidden bg-slate-900 text-white md:block">
        <div className="fg-container flex items-center justify-between py-3 text-sm">
          <Link to="/" className="flex items-center gap-2" data-testid="utility-logo-link">
            <img
              src={BRAND.logoUrl}
              alt="Flowgrid Plumbing"
              className="h-9 w-9 rounded bg-white p-1 object-contain"
            />
            <span className="font-display text-base font-extrabold tracking-tight text-white">
              Flowgrid Plumbing
            </span>
          </Link>

          <div className="flex items-center gap-8">
            <a
              href={BRAND.phoneTel}
              data-testid="utility-phone-link"
              className="flex items-center gap-3 transition-colors hover:text-blue-300"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600">
                <Phone className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Call us
                </span>
                <span className="font-bold">{BRAND.phone}</span>
              </span>
            </a>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600">
                <MapPin className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Service
                </span>
                <span className="font-bold">All of Sydney</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600">
                <Clock className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Open 24/7
                </span>
                <span className="font-bold">Emergency 24/7</span>
              </span>
            </div>
            <a
              href={BRAND.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Flowgrid Plumbing on Instagram"
              data-testid="utility-instagram-link"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 via-fuchsia-500 to-yellow-400 text-white transition-transform hover:scale-110"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href={BRAND.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Flowgrid Plumbing on Facebook"
              data-testid="utility-facebook-link"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white transition-transform hover:scale-110"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="fg-container flex h-16 items-center justify-between md:h-16">
          <Link
            to="/"
            className="flex items-center gap-2 md:hidden"
            data-testid="navbar-logo-link"
            onClick={() => setOpen(false)}
          >
            <img
              src={BRAND.logoUrl}
              alt="Flowgrid Plumbing"
              className="h-9 w-9 object-contain"
            />
            <div className="leading-tight">
              <div className="font-display text-base font-extrabold tracking-tight text-slate-900">
                Flowgrid
              </div>
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Plumbing
              </div>
            </div>
          </Link>

          <nav
            className="hidden items-center gap-1 md:flex"
            data-testid="desktop-nav"
          >
            <NavLink
              to="/"
              end
              data-testid="nav-link-home"
              className={({ isActive }) =>
                `rounded-md px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "text-blue-700" : "text-slate-800 hover:text-blue-700"
                }`
              }
            >
              Home
            </NavLink>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-md px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                    isActive
                      ? "text-blue-700"
                      : "text-slate-800 hover:text-blue-700"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact#quote"
              data-testid="navbar-quote-btn"
              className="inline-flex items-center gap-2 rounded-full bg-yellow-400 px-6 py-2.5 text-sm font-bold uppercase tracking-wider text-slate-900 transition-colors hover:bg-yellow-300"
            >
              Free Quote
            </Link>
          </div>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-900 md:hidden"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-slate-200 bg-white md:hidden"
          data-testid="mobile-menu-panel"
        >
          <nav className="fg-container flex flex-col py-3">
            <NavLink
              to="/"
              end
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-link-home"
              className={({ isActive }) =>
                `rounded-md px-3 py-3 text-base font-bold uppercase tracking-wider ${
                  isActive ? "text-blue-700" : "text-slate-800 hover:bg-slate-50"
                }`
              }
            >
              Home
            </NavLink>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-base font-bold uppercase tracking-wider ${
                    isActive
                      ? "text-blue-700"
                      : "text-slate-800 hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={BRAND.phoneTel}
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-call-link"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-4 py-3 text-base font-bold text-slate-900"
            >
              <Phone className="h-4 w-4 text-blue-600" /> {BRAND.phone}
            </a>
            <Link
              to="/contact#quote"
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-quote-btn"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-yellow-400 px-4 py-3 text-base font-bold uppercase tracking-wider text-slate-900"
            >
              Free Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
